import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play, Heart, ThumbsUp, MessageCircle, Share2, Plus, Sparkles, Coffee, ChevronLeft, ChevronRight, X, Download, Share, Users, Calendar, ExternalLink, Lock, Clock } from 'lucide-react';
import { useVideos } from '../../hooks/useVideos';
import { useEvents } from '../../hooks/useEvents';
import { useFeedPosts } from '../../hooks/useFeedPosts';
import { useVideoEngagement } from '../../hooks/useVideoEngagement';
import { buildFeed } from '../../lib/feedBuilder';
import { formattedPreview } from '../../lib/formatText';
import FormattedText, { FormattedPreview } from '../common/FormattedText';
import PostImages from '../common/PostImages';
import { firstYouTubeVideoId } from '../../lib/youtubeLinks';
import YouTubePreview from '../common/YouTubePreview';
import { buildHeroSlots } from '../../lib/heroSlots';
import { pickDailySponsor } from '../../data/sponsors';
import { youtubeEmbedSrc } from '../../lib/youtubeEmbed';
import { mountVideo } from '../../lib/hlsPlayer';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import useToast from '../../hooks/useToast';
import { useVerification } from '../../hooks/useVerification';
import Wordmark from '../common/Wordmark';
import { useUser } from '../../contexts/UserContext';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../../lib/supabase';
import AnnouncementCard from '../common/AnnouncementCard';
import TableIcon from '../common/TableIcon';
import TablesPrompt from '../common/TablesPrompt';
import PullToRefreshIndicator from '../common/PullToRefreshIndicator';
import Toast from '../common/Toast';
import CreatePostModal from '../common/CreatePostModal';
import VerifyModal from '../common/VerifyModal';
import { getRoleLabel, isDspRole } from '../../lib/roleLabels';
import TeamTag from '../common/TeamTag';
import EditedMark from '../common/EditedMark';
import VideoListItem from '../common/VideoListItem';
import EventListItem from '../common/EventListItem';
import { shareLink, shareToastMessage } from '../../lib/share';



const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};

const formatRelative = (ts) => {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  // Short date instead of a decaying day counter
  const date = new Date(ts);
  const opts = { month: 'short', day: 'numeric' };
  if (date.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return date.toLocaleDateString('en-US', opts);
};

// Skeleton loaders
const VideoSkeleton = () => (
  <div className="animate-pulse space-y-3">
    <div className="aspect-video rounded-xl" style={{ backgroundColor: 'var(--skeleton-bg)' }}></div>
    <div className="h-4 rounded w-3/4" style={{ backgroundColor: 'var(--skeleton-bg)' }}></div>
    <div className="h-3 rounded w-1/2" style={{ backgroundColor: 'var(--skeleton-bg)' }}></div>
  </div>
);

const PostSkeleton = () => (
  <div className="animate-pulse p-4 rounded-2xl" style={{ backgroundColor: 'var(--card-bg)' }}>
    <div className="flex gap-3 mb-3">
      <div className="w-10 h-10 rounded-full" style={{ backgroundColor: 'var(--skeleton-bg)' }}></div>
      <div className="flex-1 space-y-2">
        <div className="h-3 rounded w-1/4" style={{ backgroundColor: 'var(--skeleton-bg)' }}></div>
        <div className="h-2 rounded w-1/6" style={{ backgroundColor: 'var(--skeleton-bg)' }}></div>
      </div>
    </div>
    <div className="space-y-2">
      <div className="h-3 rounded w-full" style={{ backgroundColor: 'var(--skeleton-bg)' }}></div>
      <div className="h-3 rounded w-5/6" style={{ backgroundColor: 'var(--skeleton-bg)' }}></div>
    </div>
  </div>
);

// Community post card — accepts DB shape from get_feed_posts RPC
const PostCard = ({ post, isExpanded, isLiked, likeCount, isOwn, onLike, onToggleExpand, onShare }) => {
  // Never cuts a link in half — a clipped URL would still render as a live
  // link, pointing somewhere wrong.
  // The collapsed preview is cut on the parsed text (formatText.formattedPreview),
  // so bold/italic survive and a cut can never leave a dangling "**" or half a
  // link. The full post, expanded or short enough not to need cutting, renders
  // lists as real lists.
  const needsTruncation = formattedPreview(post.content, 180).truncated;
  const previewVideoId = firstYouTubeVideoId(post.content);
  const avatarColors = [
    'from-brand-primary to-brand-secondary',
    'from-emerald-500 to-teal-500',
    'from-violet-500 to-purple-500',
    'from-orange-400 to-rose-400',
  ];
  const authorName = (post.author_use_username && post.author_username) ? post.author_username : (post.author_name || 'DSP');
  const avatarColor = avatarColors[authorName.charCodeAt(0) % avatarColors.length];
  const timestamp = post.created_at ? formatRelative(post.created_at) : post.timestamp;

  // The badge lives in the headline row at ALL breakpoints. It used to be
  // moved into the author row on mobile, where — being flex-shrink-0 with an
  // untruncated table name — it left the role/agency span as the only
  // shrinkable item, so the role clipped on narrow screens.
  const tableBadge = (extra = '') => post.table_slug && (
    <Link
      to={`/community/${post.table_slug}`}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium text-secondary transition-all hover:brightness-110 active:scale-[0.97] flex-shrink-0 self-start max-w-full md:max-w-[45%] ${extra}`}
      style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
    >
      {/* Name only. The emoji read as out of place in a text pill, and the
          029 icon art is drawn for a 56px tile — at badge size it's a smudge.
          get_feed_posts still returns table_emoji; nothing renders it.

          Mobile WRAPS a long table name inside the pill; desktop truncates it.
          On mobile the badge has its own full-width line, so there is nothing
          to protect by clipping — and a clipped name has nowhere to expand to.
          Desktop still shares a row with the title, where an untruncated name
          would squeeze it. Written out rather than using `truncate` + an md:
          override, because those are separate utilities at equal specificity
          and which one wins would depend on Tailwind's output order. */}
      <span className="whitespace-normal break-words md:whitespace-nowrap md:overflow-hidden md:text-ellipsis">
        {post.table_name}
      </span>
    </Link>
  );

  return (
    <div className="rounded-2xl p-5 transition-all duration-300" style={cardStyle}>
      {/* Subject as headline, table badge top-right */}
      {/* The title owns the top of the card. On mobile the badge is not here at
          all — it moved to the action row — so the title gets the full width and
          its wrapping no longer depends on how long the table name happens to
          be. Desktop keeps both on one row. */}
      {post.subject && (
        <div className="flex items-start justify-between gap-3 mb-2.5">
          <Link
            to={`/community/${post.table_slug}/${post.id}`}
            className="font-bold text-base text-primary leading-snug text-left hover:underline transition-colors"
          >
            {post.subject}
          </Link>
          {tableBadge('hidden md:inline-flex')}
        </div>
      )}
      {/* Author row — two lines. The name gets its own line at readable weight;
          role, org, time and edit status sit on a quieter second line. The old
          single line put all five on one row at text-xs, which is why the role
          clipped on narrow screens: name and timestamp were flex-shrink-0, so
          role/agency was the only thing that could give. Splitting the rows
          removes that competition structurally rather than capping widths. */}
      <div className="flex items-start gap-2.5 mb-3">
        {post.author_avatar ? (
          <img src={post.author_avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className={`w-9 h-9 bg-gradient-to-br ${avatarColor} rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold`}>
            {authorName[0]}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <Link
              to={`/profile/${post.author_id}`}
              onClick={(e) => e.stopPropagation()}
              className="block min-w-0 text-sm font-semibold text-primary truncate hover:underline transition-colors"
            >
              {authorName}
            </Link>
            <TeamTag userId={post.author_id} />
          </div>
          <div className="flex items-center gap-1 text-xs min-w-0" style={{ color: 'var(--text-tertiary)' }}>
            {[getRoleLabel(post.author_role), post.author_agency].some(Boolean) && (
              <span className="truncate">
                {[getRoleLabel(post.author_role), post.author_agency].filter(Boolean).join(' · ')}
              </span>
            )}
            {timestamp && (
              <span className="flex-shrink-0">
                {[getRoleLabel(post.author_role), post.author_agency].some(Boolean) ? '· ' : ''}{timestamp}
              </span>
            )}
            <EditedMark at={post.edited_at} />
          </div>
        </div>
        {/* Only when there's no subject headline to carry it. */}
        {/* Desktop only. On mobile the action row carries the badge for every
            post, so rendering it here too would show it twice. */}
        {!post.subject && tableBadge('hidden md:inline-flex')}
      </div>
      <div className="mb-3">
        {isExpanded || !needsTruncation ? (
          <FormattedText text={post.content} className="leading-relaxed text-[15px] text-secondary" />
        ) : (
          <FormattedPreview
            text={post.content}
            maxChars={180}
            trailing="..."
            className="leading-relaxed text-[15px] text-secondary"
          />
        )}
        {/* Whole post, not just the visible preview, so a link past the
            180-character cut still gets its card. */}
        <PostImages images={post.images} className="mt-3" />
        {previewVideoId && <YouTubePreview videoId={previewVideoId} className="mt-3" />}
        {needsTruncation && (
          <button
            onClick={() => onToggleExpand(post.id)}
            className="text-brand-accent text-sm font-medium mt-2 transition-colors hover:text-brand-secondary"
          >
            {isExpanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>
      {/* Action row. On mobile it also carries the table badge, right-aligned
          via ml-auto. flex-wrap + gap-y means a long table name drops to its own
          line instead of squeezing the controls or overflowing — "Difficult
          Behaviors" does exactly that at 320px. Slightly tighter gap on mobile
          to buy the badge room; desktop keeps gap-6 and no badge here. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 md:gap-x-6 text-tertiary">
        {/* ThumbsUp, not Heart — this is helpful_count, and "Helpful Votes" /
            "Most Helpful" / the notification copy all say helpful. Video likes
            keep the Heart; they're a different object (video_likes).
            Your own post shows a plain count: you can't vote on it (052). */}
        {isOwn ? (
          <span className="flex items-center gap-2" title="Helpful votes on your post">
            <ThumbsUp size={20} aria-hidden="true" />
            <span className="text-sm font-medium">{likeCount}<span className="sr-only"> helpful votes</span></span>
          </span>
        ) : (
          <button
            onClick={() => onLike(post.id)}
            className="flex items-center gap-2 transition-all duration-200 hover:scale-110 active:scale-95"
            style={{ color: isLiked ? 'var(--brand-primary)' : undefined }}
            aria-label={isLiked ? 'Remove helpful' : 'Mark helpful'}
          >
            <ThumbsUp size={20} className={`transition-all duration-200 ${isLiked ? 'fill-current' : ''}`} />
            <span className="text-sm font-medium">{likeCount}</span>
          </button>
        )}
        <Link
          to={`/community/${post.table_slug}/${post.id}`}
          className="flex items-center gap-2 transition-all duration-200 hover:scale-110 active:scale-95 hover:text-primary"
        >
          <MessageCircle size={20} />
          <span className="text-sm font-medium">{post.reply_count ?? post.comments ?? 0}</span>
        </Link>
        <button
          onClick={() => onShare({
            title: post.subject || 'A post on DSP Cafe',
            path: `/community/${post.table_slug}/${post.id}`,
          })}
          className="flex items-center gap-2 transition-all duration-200 hover:scale-110 active:scale-95 hover:text-primary"
          aria-label="Share post"
        >
          <Share2 size={20} />
        </button>
        {tableBadge('md:hidden ml-auto')}
      </div>
    </div>
  );
};

// Mounts via hlsPlayer so HLS (Cloudflare) and MP4 (legacy Supabase) share one
// path. Cleanup is required — hls.js leaks XHRs, timers and a worker otherwise.
const HlsVideo = ({ video }) => {
  const hostRef = useRef(null);

  useEffect(() => {
    if (!hostRef.current) return undefined;
    const cleanup = mountVideo(hostRef.current, {
      src: video.streamHlsUrl || video.storageUrl,
      poster: video.thumbnail,
      captionUrl: video.hasCaptions ? video.captionUrl : null,
    });
    return cleanup;
  }, [video.streamHlsUrl, video.storageUrl, video.thumbnail, video.hasCaptions, video.captionUrl]);

  return <div ref={hostRef} className="absolute inset-0" />;
};

// Inline video feed card
const VideoCard = ({ video, navigate, isPlaying, onPlay, likeCount, isLiked, onToggleLike, onShare, commentCount = 0 }) => (
  <div
    className="rounded-2xl overflow-hidden transition-all duration-300"
    style={cardStyle}
  >
    <div className="relative aspect-video bg-black">
      {isPlaying && video.youtubeId ? (
        <iframe
          src={youtubeEmbedSrc(video.youtubeId)}
          title={video.title}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          style={{ border: 'none' }}
        />
      ) : isPlaying && (video.streamHlsUrl || video.storageUrl) ? (
        <HlsVideo video={video} />
      ) : (
        <>
          <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
          <button
            className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/10 hover:bg-black/30 transition-all duration-300 group"
            onClick={() => (video.youtubeId || video.streamHlsUrl || video.storageUrl) ? onPlay(video.id) : navigate(`/video/${video.id}`)}
          >
            <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 transform scale-90 group-hover:scale-100 transition-all duration-300">
              <Play size={22} fill="currentColor" style={{ color: 'var(--brand-primary)' }} />
            </div>
          </button>
          {video.duration && (
            <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/70 backdrop-blur-sm text-white text-xs rounded font-medium pointer-events-none">
              {video.duration}
            </div>
          )}
          {video.category && (
            <div className="absolute top-2 left-2 pointer-events-none">
              <span
                className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
              >
                {video.category}
              </span>
            </div>
          )}
          {video.isCurated && (
            <div className="absolute top-2 right-2 pointer-events-none">
              <span
                className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: 'var(--brand-primary)', backdropFilter: 'blur(8px)' }}
              >
                Staff Pick
              </span>
            </div>
          )}
        </>
      )}
    </div>
    <div className="p-4">
      <Link
        to={`/video/${video.id}`}
        className="block font-semibold text-base mb-1.5 line-clamp-2 text-primary hover:text-brand-accent transition-colors leading-snug text-left w-full"
      >
        {video.title}
      </Link>
      <div className="flex items-center justify-between">
        <span className="text-sm text-tertiary truncate mr-2">{video.creator}</span>
        <span className="text-xs text-tertiary flex-shrink-0">{video.views != null ? `${video.views} views` : ''}{video.timestamp ? `${video.views != null ? ' · ' : ''}${video.timestamp}` : ''}</span>
      </div>
      {video.commentary && (
        <p className="text-sm mt-2 leading-relaxed line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
          {video.commentary}
        </p>
      )}
      <div className="flex gap-5 text-tertiary mt-3 pt-3" style={{ borderTop: '1px solid var(--card-border)' }}>
        <button
          onClick={() => onToggleLike(video.id)}
          className={`flex items-center gap-1.5 transition-all duration-200 hover:scale-110 active:scale-95 ${isLiked ? 'text-rose-500' : 'hover:text-rose-500'}`}
        >
          <Heart size={18} className={isLiked ? 'fill-current' : ''} />
          {likeCount > 0 && <span className="text-xs font-medium">{likeCount}</span>}
        </button>
        <Link
          to={`/video/${video.id}`}
          className="flex items-center gap-1.5 transition-all duration-200 hover:scale-110 active:scale-95 hover:text-primary"
        >
          <MessageCircle size={18} />
          {commentCount > 0 && <span className="text-xs font-medium">{commentCount}</span>}
        </Link>
        <button
          onClick={() => onShare({ title: video.title, path: `/video/${video.id}` })}
          className="flex items-center gap-1.5 transition-all duration-200 hover:scale-110 active:scale-95 hover:text-primary"
          aria-label="Share video"
        >
          <Share2 size={18} />
        </button>
      </div>
    </div>
  </div>
);

// Sidebar: compact video row for Trending
// Safari-only signals — inside the Capacitor native app, navigator.userAgent
// still says "iPhone" and navigator.standalone is undefined (that's a Safari
// PWA API, not a Capacitor one), so both would otherwise look like "Safari,
// not yet added to home screen" even though the real native app is running.
const isIOS = !Capacitor.isNativePlatform()
  && /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
const isIOSStandalone = window.navigator.standalone === true;

// One hero slot. Renders an <a> for an external destination (event
// registration) and a div+navigate for an internal one, so a registration link
// behaves like a real link — long-press, open-in-new-tab, and the system
// browser inside Capacitor.
const HeroCard = ({ slot, navigate }) => {
  const external = !!slot.href;
  const Wrapper = external ? 'a' : 'div';
  const wrapperProps = external
    ? { href: slot.href, target: '_blank', rel: 'noopener noreferrer' }
    : { onClick: () => navigate(slot.to) };

  const light = slot.variant === 'light';

  const badgeStyle = light
    ? { backgroundColor: 'var(--brand-primary)' }
    : slot.kind === 'event'
    ? { backgroundColor: slot.accent }
    : { backgroundColor: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)' };

  // Light variant: a logo on a pale ground with dark type. The photographic
  // treatment (full-bleed crop + dark scrim + white text) would mangle a logo —
  // these arrive on white or transparent grounds at aspect ratios from 1.4:1 to
  // 4.5:1, so the logo is CONTAINED and given its own white plate instead.
  if (light) {
    return (
      <Wrapper
        {...wrapperProps}
        className="flex-shrink-0 w-[88%] md:w-full cursor-pointer group active:scale-[0.98] transition-transform block"
        style={{ scrollSnapAlign: 'start' }}
      >
        {/* Sponsor slide — the mark centred on white, with the designation and
            the action in opposite corners. No footer, no information panel: two
            of three sponsors have only a name and a URL, so a panel had nothing
            to fill it with and read as padding.

            ⚠️ Corner-clearance is why the caps are what they are. At 696x216 the
            badge occupies x 12-142 / y 12-38 and the Visit pill x 544-684 /
            y 178-204, and the carousel arrows sit at x 8-44 and 652-688. A
            CENTRED logo of width w spans (696-w)/2 .. (696+w)/2, so max-w 380
            keeps its box at 158..538 — clear of both corners and both arrows —
            while max-h 176 fills the card vertically. Measured result:

              NADSP            268 x 176
              NAQ              319 x 176   (the 300-320 target)
              IntellectAbility 380 x  85   (4.5:1 wordmark; short, so it also
                                            clears the corners vertically)

            Similar prominence, not identical widths — the marks are 1.5:1 to
            4.5:1 and object-contain never crops or distorts them. Recompute
            these if the card height or the corner elements change. */}
        <div
          className="relative rounded-2xl overflow-hidden aspect-[16/9] md:aspect-auto md:h-[216px]"
          style={{
            // Solid white, not --card-bg (which is 80% white over the cream
            // gradient). The NADSP logo is a JPEG with pure white baked in, so
            // on a tinted card it would show as a visible white rectangle.
            backgroundColor: '#FFFFFF',
            border: '1px solid var(--card-border)',
          }}
        >
          <div className="absolute top-3 left-3 z-10">
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={badgeStyle}>
              {slot.badge}
            </span>
          </div>

          {/* Centred in the whole card, both axes. */}
          <div className="absolute inset-0 flex items-center justify-center px-12">
            {slot.logo && (
              <img
                src={slot.logo}
                alt={slot.title}
                className="max-h-24 md:max-h-[176px] max-w-[72%] md:max-w-[380px] w-auto object-contain"
                loading="lazy"
              />
            )}
          </div>

          {/* Reads as a button, but stays a span — the whole card is already the
              anchor, and an <a> inside an <a> is invalid HTML. */}
          <div className="absolute bottom-3 right-3 z-10">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors group-hover:brightness-95"
              style={{
                color: 'var(--brand-primary)',
                backgroundColor: 'var(--pill-bg)',
                border: '1px solid var(--pill-border)',
              }}
            >
              {slot.cta}
              <ExternalLink size={12} />
            </span>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      {...wrapperProps}
      className="flex-shrink-0 w-[88%] md:w-full cursor-pointer group active:scale-[0.98] transition-transform block"
      style={{ scrollSnapAlign: 'start' }}
    >
      <div className="relative rounded-2xl overflow-hidden aspect-[16/9] md:aspect-auto md:h-[216px]">
        {slot.image ? (
          <img
            src={slot.image}
            alt=""
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          // No art — a flat accent ground still reads as a designed card, and
          // an event without an image is the common case.
          <div
            className="w-full h-full"
            style={{ background: `linear-gradient(135deg, ${slot.accent} 0%, var(--brand-secondary) 100%)` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={badgeStyle}>
            {slot.badge}
          </span>
        </div>
        {/* Banner proportions on desktop leave a lot of width beside the text,
            so the CTA moves to the right rather than sitting under it. Stacks
            on mobile, where the card is still 16/9 and the width isn't there. */}
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
          <div className="md:flex md:items-end md:justify-between md:gap-6">
            <div className="min-w-0">
              <h3 className="text-white font-bold text-lg md:text-xl mb-1 leading-tight line-clamp-2">{slot.title}</h3>
              {/* When it happens outranks who's running it: this is the line
                  someone reads to decide whether they can attend. Carries the
                  timezone, since the time is rendered in the viewer's zone. */}
              {slot.when && (
                <p className="text-white text-xs md:text-sm font-semibold mb-0.5">{slot.when}</p>
              )}
              {slot.subtitle && (
                <p className="text-white/70 text-xs line-clamp-1">{slot.subtitle}</p>
              )}
              {slot.sponsorName && (
                <p className="text-white/50 text-xs mt-0.5">Presented by {slot.sponsorName}</p>
              )}
            </div>
            <span
              className="inline-block mt-2.5 md:mt-0 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all group-hover:brightness-110 flex-shrink-0"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {slot.cta}
            </span>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

const FeedScreen = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // The desktop sidebar is sticky only while it fits the window. It used to
  // scroll inside its own box once taller, but macOS hides that scrollbar, so
  // members on shorter screens saw Today's Picks cut off with no cue that more
  // was there. When it doesn't fit it scrolls with the page instead.
  const sidebarRef = useRef(null);
  const [sidebarFits, setSidebarFits] = useState(true);
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return undefined;
    // 80px = the 4rem sticky offset under TopNav + 1rem breathing room.
    const check = () => setSidebarFits(el.scrollHeight <= window.innerHeight - 80);
    check();
    window.addEventListener('resize', check);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    return () => {
      window.removeEventListener('resize', check);
      ro?.disconnect();
    };
  }, []);

  const { toastMsg, showToast } = useToast();
  const { videos, loading: videosLoading, refresh: refreshVideos } = useVideos();
  const { posts, loading: postsLoading, refresh: refreshPosts } = useFeedPosts();
  const { events, refresh: refreshEvents } = useEvents({ limit: 8 });
  // Desktop sidebar widget. Was a hardcoded 4-entry mock, so it ignored real
  // tables and real activity — and had no image_url, which is what the icon
  // art needs. Reuses the RPC CommunityScreen already calls; no migration.
  const [suggestedTables, setSuggestedTables] = useState([]);
  useEffect(() => {
    if (!isDesktop) return;   // the widget is desktop-only; don't fetch on mobile
    let cancelled = false;
    supabase.rpc('get_community_tables_with_membership').then(({ data, error }) => {
      if (error) { console.error('get_community_tables_with_membership:', error); return; }
      if (cancelled) return;
      // Still ranked by activity. Under the name "Find your table" a
      // discovery ranking (tables you have NOT joined first) would serve the
      // card's purpose better — one line to change when that's wanted.
      const ranked = [...(data ?? [])]
        .sort((a, b) => (b.post_count ?? 0) - (a.post_count ?? 0))
        .slice(0, 3);
      setSuggestedTables(ranked);
    });
    return () => { cancelled = true; };
  }, [isDesktop]);
  const { verified, showVerifyModal, setShowVerifyModal, requireVerified } = useVerification();
  const { session, userData } = useUser();
  // Single paint: wait for both sources so the rotated order never reshuffles underfoot
  const feedLoading = postsLoading || videosLoading;
  // Today's Picks are the first three of the rotation. They move to the BACK of
  // the inline video order so the first video card isn't a repeat of the shelf
  // (mobile) or the sidebar (desktop) sitting right above it. Moved, not
  // dropped: while the library is under videoCap + 3, they still appear inline
  // further down rather than shrinking the feed's video budget.
  const todaysPicks = useMemo(() => videos.slice(0, 3), [videos]);
  const feedItems = useMemo(
    () => buildFeed(posts, [...videos.slice(3), ...videos.slice(0, 3)]),
    [posts, videos]
  );
  // Mobile shelf sits after the SECOND post: conversations still lead, but the
  // shelf lands in the first scroll. It used to render after the whole feed —
  // below "You're all caught up" — which on a 50-post feed meant never. With
  // fewer than two posts it goes at the end of what there is.
  const picksAfterIndex = useMemo(() => {
    let seen = 0;
    const i = feedItems.findIndex(({ type }) => type === 'post' && ++seen === 2);
    return i === -1 ? feedItems.length - 1 : i;
  }, [feedItems]);

  // Soonest first. get_upcoming_events already filters out anything finished
  // and orders by start, but sorting here keeps this correct if that ordering
  // ever changes — the card's whole claim is "what's next".
  const upcomingEvents = useMemo(
    () => [...events].sort((a, b) => a.startsAt - b.startsAt).slice(0, 3),
    [events]
  );
  // One Founding Sponsor per user per day, on the same seed the video rotation
  // uses — three sponsors share one slot so events and Staff Picks keep theirs.
  const sponsor = useMemo(
    () => pickDailySponsor(session?.user?.id),
    [session?.user?.id]
  );
  const heroSlots = useMemo(
    () => buildHeroSlots({ videos, events, sponsor }),
    [videos, events, sponsor]
  );
  const feedVideoIds = useMemo(
    () => (feedLoading ? [] : feedItems.filter((fi) => fi.type === 'video').map((fi) => fi.item.id)),
    [feedLoading, feedItems]
  );
  const { engagement, toggleLike, logPlay } = useVideoEngagement(feedVideoIds);
  const { showPrompt, handleInstall, dismissPrompt } = useInstallPrompt();
  const [showIOSBanner, setShowIOSBanner] = useState(() => {
    if (!isIOS || isIOSStandalone) return false;
    const ts = localStorage.getItem('pwa_dismissed_at');
    if (ts && Date.now() - parseInt(ts, 10) < 14 * 24 * 60 * 60 * 1000) return false;
    return true;
  });
  const [showCreatePost, setShowCreatePost] = useState(false);

  // Every feed entry point to CreatePostModal goes through here. Unverified
  // members get VerifyModal (like CommunityScreen); a pending one has nothing
  // left to do, so they get a toast rather than the methods again.
  const verificationPending = !verified && userData?.verification_status === 'pending';
  // Verification is for DSPs, so a non-DSP isn't told to verify — the tap
  // still opens VerifyModal, which explains.
  const readOnly = !verified && !verificationPending && !isDspRole(userData?.role);
  const openCreatePost = () => {
    if (verificationPending) {
      showToast("Your verification is being reviewed. You can post once it's approved.");
      return;
    }
    if (requireVerified()) setShowCreatePost(true);
  };
  const [playingVideoId, setPlayingVideoId] = useState(null);
  // { [postId]: { liked, count } } — seeded from get_feed_posts rows
  const [likeState, setLikeState] = useState({});
  const [expandedPosts, setExpandedPosts] = useState(new Set());
  const [activeHero, setActiveHero] = useState(0);
  const heroRef = useRef(null);
  const headerHidden = useScrollDirection();

  useEffect(() => {
    const container = heroRef.current;
    if (!container) return;
    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.children[0]?.offsetWidth || 1;
      setActiveHero(Math.round(scrollLeft / cardWidth));
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const hapticFeedback = () => {
    if (navigator.vibrate) navigator.vibrate(10);
  };

  const refreshFeed = useCallback(
    () => Promise.all([refreshPosts(), refreshVideos(), refreshEvents()]),
    [refreshPosts, refreshVideos, refreshEvents]
  );

  const { pull, progress, armed, dragging, refreshing } = usePullToRefresh(refreshFeed, {
    enabled: !isDesktop,
  });

  // Buzz once at the moment the pull arms, the way the native gesture does.
  const armedRef = useRef(false);
  useEffect(() => {
    if (armed && !armedRef.current && navigator.vibrate) navigator.vibrate(10);
    armedRef.current = armed;
  }, [armed]);

  // The feed fetches once on mount, and a Capacitor WebView is resumed rather
  // than reloaded — without this a backgrounded app comes back to a stale feed
  // indefinitely. Throttled so tab-switching on the web isn't an RPC per focus.
  const lastRefresh = useRef(Date.now());
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefresh.current < 60_000) return;
      lastRefresh.current = Date.now();
      refreshFeed();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshFeed]);

  const scrollToHero = (idx) => {
    const container = heroRef.current;
    if (!container) return;
    const clamped = Math.max(0, Math.min(idx, heroSlots.length - 1));
    const cardWidth = container.children[0]?.offsetWidth || 0;
    container.scrollTo({ left: clamped * cardWidth, behavior: 'smooth' });
    setActiveHero(clamped);
  };

  const handlePlay = (videoId) => {
    logPlay(videoId);
    setPlayingVideoId(videoId);
  };

  // Seed like state from the RPC rows (user_liked + helpful_count) once per post
  useEffect(() => {
    if (!posts.length) return;
    setLikeState(prev => {
      const next = { ...prev };
      posts.forEach(p => {
        if (!next[p.id]) next[p.id] = { liked: !!p.user_liked, count: p.helpful_count ?? 0 };
      });
      return next;
    });
  }, [posts]);

  const flipLike = (postId) => {
    setLikeState(prev => {
      const cur = prev[postId] || { liked: false, count: 0 };
      return {
        ...prev,
        [postId]: { liked: !cur.liked, count: Math.max(0, cur.count + (cur.liked ? -1 : 1)) },
      };
    });
  };

  // Not verification-gated: sharing a link is a read-side action, same
  // reasoning as follows and video likes.
  const handleShare = useCallback(async ({ title, path }) => {
    const msg = shareToastMessage(await shareLink({ title, path }));
    if (msg) showToast(msg);
  }, [showToast]);

  const handleLike = async (postId) => {
    if (!requireVerified()) return;
    hapticFeedback();
    flipLike(postId); // optimistic
    const { data: newCount, error } = await supabase.rpc('toggle_post_like', { p_post_id: postId });
    if (error) {
      console.error('toggle_post_like:', error);
      flipLike(postId); // revert
    } else if (typeof newCount === 'number') {
      setLikeState(prev => ({ ...prev, [postId]: { ...prev[postId], count: newCount } }));
    }
  };

  const toggleExpand = (postId) => {
    setExpandedPosts(prev => {
      const next = new Set(prev);
      next.has(postId) ? next.delete(postId) : next.add(postId);
      return next;
    });
  };

  const dismissIOSBanner = () => {
    localStorage.setItem('pwa_dismissed_at', Date.now().toString());
    setShowIOSBanner(false);
  };

  // Today's Picks — mobile only (desktop has it in the sidebar). Placed inline in
  // the feed by picksAfterIndex; see the comment there.
  const picksShelf = todaysPicks.length > 0 && (
    <div className="md:hidden rounded-2xl overflow-hidden" style={cardStyle}>
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <h2 className="font-semibold text-base text-primary">Today's Picks</h2>
        <Sparkles className="text-rose-400" size={18} />
      </div>
      <div className="flex overflow-x-auto scrollbar-hide px-4 pb-4 gap-3">
        {todaysPicks.map(video => (
          <Link
            key={video.id}
            to={`/video/${video.id}`}
            className="flex-shrink-0 w-40 cursor-pointer group"
          >
            <div
              className="rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
              style={{ border: '1px solid var(--card-border)' }}
            >
              <div className="relative aspect-video bg-black">
                <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/25 transition-all duration-300 flex items-center justify-center">
                  <div className="bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
                    <Play size={14} fill="currentColor" style={{ color: 'var(--brand-primary)' }} />
                  </div>
                </div>
                {video.duration && (
                  <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 bg-black/70 text-white text-xs rounded">
                    {video.duration}
                  </div>
                )}
              </div>
              <div className="p-2">
                <h4 className="font-semibold text-xs line-clamp-2 text-primary group-hover:text-brand-accent transition-colors leading-snug mb-0.5">
                  {video.title}
                </h4>
                <p className="text-xs text-tertiary truncate">{video.creator}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <div
      className="min-h-screen pb-nav"
    >
      {/* Status bar background — fixed, always covers safe area even when header is hidden */}
      <div
        className="fixed top-0 left-0 right-0 z-30 md:hidden"
        style={{
          height: 'var(--sat)',
          backgroundColor: 'var(--header-bg)',
        }}
      />

      {/* Header — mobile only */}
      <div
        className="sticky top-0 z-20 md:hidden"
        style={{
          backgroundColor: 'var(--header-bg)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          paddingTop: 'var(--sat)',
          transform: headerHidden ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.3s ease',
        }}
      >
        <div className="max-w-2xl mx-auto px-4 py-2">
          <div className="flex items-center min-h-[40px]">
            <h1 className="text-[22px] text-white">
              <Wordmark />
            </h1>
          </div>
        </div>
      </div>

      {/* Pull-to-refresh. The indicator is painted behind the content, which
          translates down to reveal it — transform only, so nothing reflows. */}
      <div className="relative">
        <PullToRefreshIndicator
          pull={pull}
          progress={progress}
          armed={armed}
          refreshing={refreshing}
        />

      {/* ── Desktop: two-column grid │ Mobile: single column ── */}
      <div
        className="md:px-6 md:pt-5 md:pb-8 md:grid md:grid-cols-[1fr_320px] md:gap-6 md:items-start"
        style={{
          transform: pull > 0 ? `translateY(${pull}px)` : undefined,
          transition: dragging ? 'none' : 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: dragging || refreshing ? 'transform' : undefined,
          position: 'relative',
          zIndex: 1,
        }}
      >

        {/* ═══ LEFT: Main feed ═══ */}
        {/* min-w-0: a 1fr track won't shrink below its content's min-size
            without it, so one long unbreakable string would widen the column
            past its track and push the sidebar out. */}
        <div className="min-w-0">
          {/* Hero Carousel — composed from real content; renders nothing when
              there is none, rather than padding itself with filler. */}
          {heroSlots.length > 0 && (
          <div className="pt-5 pb-2 md:pt-0 md:pb-4">
            <div className="relative">
              {/* Prev arrow */}
              <button
                onClick={() => scrollToHero(activeHero - 1)}
                disabled={activeHero === 0}
                className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full transition-all duration-200 hover:scale-110 active:scale-95 disabled:opacity-0 disabled:pointer-events-none"
                style={{ backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)' }}
              >
                <ChevronLeft size={18} className="text-white" />
              </button>
              {/* Next arrow */}
              <button
                onClick={() => scrollToHero(activeHero + 1)}
                disabled={activeHero === heroSlots.length - 1}
                className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full transition-all duration-200 hover:scale-110 active:scale-95 disabled:opacity-0 disabled:pointer-events-none"
                style={{ backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)' }}
              >
                <ChevronRight size={18} className="text-white" />
              </button>

              {/* Carousel track */}
              <div
                ref={heroRef}
                className="flex overflow-x-auto scrollbar-hide pl-4 md:pl-0 gap-3 md:gap-0"
                style={{
                  scrollSnapType: 'x mandatory',
                  scrollPaddingLeft: isDesktop ? 0 : '1rem',
                }}
              >
                {heroSlots.map((slot) => (
                  <HeroCard key={slot.key} slot={slot} navigate={navigate} />
                ))}
              </div>
            </div>

            {/* Page dots — pointless with a single slot */}
            <div className={`justify-center gap-1.5 mt-3 ${heroSlots.length > 1 ? 'flex' : 'hidden'}`}>
              {heroSlots.map((_, idx) => (
                <div
                  key={idx}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: idx === activeHero ? '1.5rem' : '0.375rem',
                    backgroundColor: idx === activeHero ? 'var(--brand-primary)' : 'var(--text-tertiary)',
                    opacity: idx === activeHero ? 1 : 0.3,
                  }}
                />
              ))}
            </div>
          </div>
          )}

          {/* Pinned tester announcement — sits under the featured carousel, on
              the plain card surface so it reads as a note, not a promo. */}
          <AnnouncementCard />

          {/* Install banner — Android/Chrome */}
          {showPrompt && (
            <div className="px-4 md:px-0 pt-1">
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
              >
                <Download size={18} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />
                <p className="flex-1 text-sm text-secondary">Add DSP Cafe to your home screen</p>
                <button
                  onClick={handleInstall}
                  className="px-3 py-1 rounded-lg text-xs font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] flex-shrink-0"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                >
                  Install
                </button>
                <button onClick={dismissPrompt} aria-label="Dismiss install prompt" className="p-1 transition-all hover:scale-110 active:scale-95 flex-shrink-0">
                  <X size={16} style={{ color: 'var(--text-tertiary)' }} />
                </button>
              </div>
            </div>
          )}

          {/* Install banner — iOS Safari */}
          {showIOSBanner && (
            <div className="px-4 md:px-0 pt-1">
              <div
                className="flex items-start gap-3 px-4 py-3 rounded-xl"
                style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
              >
                <Share size={18} style={{ color: 'var(--brand-primary)', flexShrink: 0, marginTop: 1 }} />
                <p className="flex-1 text-sm text-secondary">
                  Tap <span className="font-semibold">Share</span> then <span className="font-semibold">Add to Home Screen</span> to install DSP Cafe
                </p>
                <button onClick={dismissIOSBanner} aria-label="Dismiss install instructions" className="p-1 transition-all hover:scale-110 active:scale-95 flex-shrink-0">
                  <X size={16} style={{ color: 'var(--text-tertiary)' }} />
                </button>
              </div>
            </div>
          )}

          {/* New members who have joined no table yet (renders nothing otherwise). */}
          <TablesPrompt userId={session?.user?.id} />

          {/* Composer entry point — the visible way in. Opens the same
              CreatePostModal the FAB does, so drafts, the verified gate and
              table selection all come along unchanged; this is an entry point,
              not a second compose implementation. Coexists with the FAB on
              mobile, matching the table detail screen. Unverified and pending
              members see why up front (openCreatePost) — before, they wrote a
              whole post and only learned at submit that it would be refused. */}
          <div className="px-4 md:px-0 pt-1 pb-1">
            <button
              onClick={openCreatePost}
              className="w-full flex items-center gap-3 p-3 md:p-4 rounded-2xl text-left transition-all hover-surface focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ ...cardStyle, outlineColor: 'var(--brand-primary)' }}
            >
              {userData?.avatar_url ? (
                <img src={userData.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div
                  className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))' }}
                >
                  {(userData?.name || session?.user?.email || 'D')[0].toUpperCase()}
                </div>
              )}
              <span className="flex-1 min-w-0 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                {verified
                  ? 'Ask a question or share an experience…'
                  : verificationPending
                    ? 'You can post once your verification is approved'
                    : readOnly
                      ? 'Posting is for verified DSPs'
                      : 'Verify your account to start posting'}
              </span>
              <span
                className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold"
                style={verificationPending || readOnly
                  ? { backgroundColor: 'var(--input-bg)', color: 'var(--text-secondary)' }
                  : { backgroundColor: 'var(--brand-primary)', color: '#fff' }}
              >
                {verificationPending
                  ? <><Clock size={12} aria-hidden="true" /> Verification pending</>
                  : verified
                    ? 'Post'
                    : readOnly
                      ? 'Read only'
                      : <><Lock size={12} aria-hidden="true" /> Verify to post</>}
              </span>
            </button>
          </div>

          {/* Feed content */}
          <div className="px-4 md:px-0 py-3 space-y-4">
            {feedLoading ? (
              <>
                <PostSkeleton />
                <VideoSkeleton />
                <PostSkeleton />
                <PostSkeleton />
              </>
            ) : (
              <>
                {/* Quiet community, videos still flowing — inline compose nudge */}
                {posts.length === 0 && feedItems.length > 0 && (
                  <div className="flex items-center justify-between gap-3 p-4 rounded-2xl" style={cardStyle}>
                    <p className="text-sm text-secondary">No posts yet — be the first to start a conversation</p>
                    <button
                      onClick={openCreatePost}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex-shrink-0 transition-all hover:brightness-110 active:scale-[0.98]"
                      style={{ backgroundColor: 'var(--brand-primary)' }}
                    >
                      New Post
                    </button>
                  </div>
                )}

                {/* Merged post + video feed */}
                {feedItems.map(({ type, item }, i) => (
                  <React.Fragment key={`${type}-${item.id}`}>
                  {type === 'post' ? (
                    <PostCard
                      post={item}
                      isExpanded={expandedPosts.has(item.id)}
                      isLiked={likeState[item.id]?.liked ?? !!item.user_liked}
                      likeCount={likeState[item.id]?.count ?? item.helpful_count ?? 0}
                      isOwn={!!session?.user?.id && item.author_id === session.user.id}
                      onLike={handleLike}
                      onToggleExpand={toggleExpand}
                      onShare={handleShare}
                    />
                  ) : (
                    <VideoCard
                      video={item}
                      navigate={navigate}
                      isPlaying={playingVideoId === item.id}
                      onPlay={handlePlay}
                      likeCount={engagement[String(item.id)]?.likeCount ?? 0}
                      isLiked={engagement[String(item.id)]?.liked ?? false}
                      commentCount={engagement[String(item.id)]?.commentCount ?? 0}
                      onToggleLike={toggleLike}
                      onShare={handleShare}
                    />
                  )}
                  {i === picksAfterIndex && picksShelf}
                  </React.Fragment>
                ))}

                {/* End of feed */}
                {feedItems.length > 0 && (
                  <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <Coffee size={28} style={{ color: 'var(--text-tertiary)' }} strokeWidth={1.5} />
                    <p className="text-sm font-medium text-tertiary">You're all caught up</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <button
                        onClick={() => navigate('/explore/videos')}
                        className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
                        style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--brand-primary)', border: '1px solid var(--pill-border)' }}
                      >
                        Browse the video library
                      </button>
                      <button
                        onClick={() => navigate('/community')}
                        className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
                        style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--brand-primary)', border: '1px solid var(--pill-border)' }}
                      >
                        Visit the tables
                      </button>
                    </div>
                  </div>
                )}

                {/* Empty state — both sources empty */}
                {feedItems.length === 0 && (
                  <div className="flex flex-col items-center gap-2 py-12 text-center">
                    <Coffee size={32} style={{ color: 'var(--text-tertiary)' }} strokeWidth={1.5} />
                    <p className="text-sm font-medium text-tertiary">No posts yet</p>
                    <p className="text-xs text-tertiary opacity-60">Be the first to post in a table</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ═══ RIGHT: Sidebar (desktop only) ═══ */}
        {/* Sticky only while it fits (sidebarFits, above). Three cards
            (Find your table + Upcoming events + Today's picks) run ~800px+,
            more than a 768px-tall window; a sticky column that tall leaves its
            bottom unreachable, and an internal scroll box was tried and read as
            cut off (macOS hides the scrollbar). So it scrolls with the page. */}
        <aside
          ref={sidebarRef}
          className={`hidden md:flex flex-col gap-4 self-start ${sidebarFits ? 'sticky top-[4rem]' : ''}`}
        >

          {suggestedTables.length > 0 && (
          <div className="rounded-2xl overflow-hidden" style={cardStyle}>
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-base text-primary">
                  <Link to="/community" className="transition-opacity hover:opacity-70">
                    Find your table
                  </Link>
                </h2>
                <Users size={16} style={{ color: 'var(--text-tertiary)' }} />
              </div>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                A place to connect over what matters to you.
              </p>
            </div>
            {/* description comes free — get_community_tables_with_membership has
                always returned it; the old "Popular Tables" card just showed a
                name and a Visit pill, which told a new member nothing about
                where they belong. */}
            <div className="px-4 space-y-1">
              {suggestedTables.map((table) => (
                <Link
                  key={table.id ?? table.slug}
                  to={`/community/${table.slug}`}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all duration-200 hover:brightness-95 active:scale-[0.98] group"
                  style={{ backgroundColor: 'var(--input-bg)' }}
                >
                  <TableIcon table={table} px={32} radius="rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary truncate">{table.name}</p>
                    {table.description && (
                      <p className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                        {table.description}
                      </p>
                    )}
                  </div>
                  <ChevronRight size={16} className="flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
                </Link>
              ))}
            </div>
            <Link
              to="/community"
              className="block px-4 py-2.5 mt-2 text-xs font-semibold text-center transition-opacity hover:opacity-70"
              style={{ borderTop: '1px solid var(--card-border)', color: 'var(--brand-primary)' }}
            >
              Explore all tables
            </Link>
          </div>
          )}

          {/* Upcoming Events — chronological, so this reads as a schedule while
              the hero carousel stays the promo slot. Overlap with the hero is
              accepted deliberately: the same event may lead the carousel AND
              appear here, which is normal for a banner-plus-schedule layout and
              keeps this card populated while the calendar is still small.
              get_upcoming_events already self-expires finished events (031), so
              nothing stale can appear. Hides entirely when empty, matching
              Popular Tables. */}
          {upcomingEvents.length > 0 && (
          <div className="rounded-2xl overflow-hidden" style={cardStyle}>
            <div className="flex items-center justify-between px-4 pt-4 pb-3">
              <h2 className="font-semibold text-base text-primary">Upcoming Events</h2>
              <Calendar size={16} style={{ color: 'var(--text-tertiary)' }} />
            </div>
            <div className="px-4 pb-3 space-y-4">
              {upcomingEvents.map((event, i) => (
                <React.Fragment key={event.id}>
                  <EventListItem event={event} />
                  {i < upcomingEvents.length - 1 && <div style={{ borderTop: '1px solid var(--card-border)' }} />}
                </React.Fragment>
              ))}
            </div>
            <Link
              to="/explore/events"
              className="block px-4 py-2.5 text-xs font-semibold text-center transition-opacity hover:opacity-70"
              style={{ borderTop: '1px solid var(--card-border)', color: 'var(--brand-primary)' }}
            >
              View all events
            </Link>
          </div>
          )}

          {/* Today's Picks */}
          {!feedLoading && (
          <div className="rounded-2xl overflow-hidden" style={cardStyle}>
            <div className="flex items-center justify-between px-4 pt-4 pb-3">
              <h2 className="font-semibold text-base text-primary">Today's Picks</h2>
              <Sparkles className="text-rose-400" size={18} />
            </div>
            <div className="px-4 pb-4 space-y-4">
              {todaysPicks.map((video, i) => (
                <React.Fragment key={video.id}>
                  <VideoListItem video={video} />
                  {i < 2 && <div style={{ borderTop: '1px solid var(--card-border)' }} />}
                </React.Fragment>
              ))}
            </div>
          </div>
          )}

          {/* Popular Tables */}

        </aside>
      </div>
      </div>

      {/* FAB — mobile only, matching CommunityScreen. It opens the exact same
          CreatePostModal as the composer bar above the feed, with the same
          default step, so on desktop it was a duplicate control stranded in the
          right margin: the shell is centred at 1088px, and `right-6` pins the
          button to the viewport edge, far from the column it acts on. Mobile
          keeps it because the composer scrolls away there and the thumb is
          already at the bottom of the screen. */}
      <button
        onClick={openCreatePost}
        className="fixed bottom-24 right-6 w-14 h-14 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-full shadow-2xl z-50 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 group md:hidden"
        title="Create new post"
        aria-label="Create new post"
      >
        <Plus className="text-white transition-transform duration-300 group-hover:rotate-90" size={26} strokeWidth={2.5} />
      </button>

      <Toast message={toastMsg} />
      {showVerifyModal && <VerifyModal onClose={() => setShowVerifyModal(false)} />}
      {showCreatePost && <CreatePostModal onClose={() => setShowCreatePost(false)} />}
    </div>
  );
};

export default FeedScreen;
